type ProfileConfig = {
  userLoggedIn?: boolean;
  beatlineSettings?: {
    auth?: { loggedIn?: number | boolean };
  };
};

function t(key: string, fallback?: string): string {
  try {
    const fn = (window as any).t;
    if (typeof fn === 'function') return fn(key, fallback || key);
  } catch (_) {}
  return fallback || key;
}

function escapeHtml(value: unknown): string {
  const div = document.createElement('div');
  div.textContent = String(value ?? '');
  return div.innerHTML;
}

function nativeSpotifyProfileMode(): boolean {
  try {
    const w = window as any;
    const native = !!(w.beatlineViteConfig?.nativeApp || w.beatlineSettings?.app?.isNativeApp || /(?:^|[?&])native_app=1(?:&|$)/.test(window.location.search) || w.BeatLineSpotify || w.Capacitor);
    if (!native) return false;
    // C27: in the native Android app, the profile should prefer Spotify stats whenever
    // Spotify mode is available. The localStorage preference can be stale after app
    // rebuilds/WebView cache restores, so do not use it as the only source of truth.
    const pref = localStorage.getItem('beatline_playback_mode') || localStorage.getItem('beatline_spotify_playback_mode');
    return pref === 'spotify' || !!w.BeatLineSpotify || /(?:^|[?&])native_app=1(?:&|$)/.test(window.location.search);
  } catch (_) {
    return false;
  }
}

function displaySourceForProfile(pl: any): string {
  const source = pl?.source || 'spotify';
  if (nativeSpotifyProfileMode() && pl?.modes?.spotify && (parseInt(String(pl.modes.spotify.total || 0), 10) || 0) > 0) {
    return 'spotify';
  }
  return source;
}

function isLoggedIn(config: ProfileConfig): boolean {
  const app = document.getElementById('beatline-app') as HTMLElement | null;
  const serverLoggedIn = !!config.userLoggedIn || !!config.beatlineSettings?.auth?.loggedIn || app?.dataset.userLoggedIn === '1';
  const heardLoggedIn = !!(window as any).beatlineHeardLoggedIn;
  return serverLoggedIn || heardLoggedIn;
}

function showBeatlineDialog(title: string, message: string, opts: { noticeOnly?: boolean; confirmLabel?: string; cancelLabel?: string } = {}): Promise<boolean> {
  return new Promise((resolve) => {
    const existing = document.querySelector('.reset-playlist-modal[data-ts-profile-dialog="1"]');
    if (existing && existing.parentNode) existing.parentNode.removeChild(existing);

    const modal = document.createElement('div');
    modal.className = 'reset-playlist-modal';
    modal.dataset.tsProfileDialog = '1';
    const noticeOnly = !!opts.noticeOnly;
    const confirmLabel = opts.confirmLabel || (noticeOnly ? 'OK' : t('btn_reset', 'Reset'));
    const cancelLabel = opts.cancelLabel || t('btn_cancel', 'Annuleren');
    modal.innerHTML = `
      <div class="reset-modal-overlay"></div>
      <div class="reset-modal-content glass-card beatline-styled-popup-card" role="dialog" aria-modal="true">
        <h2 class="reset-modal-title">${escapeHtml(title)}</h2>
        <p class="reset-modal-message">${escapeHtml(message)}</p>
        <div class="reset-modal-buttons ${noticeOnly ? 'is-notice' : ''}">
          ${noticeOnly ? '' : `<button type="button" class="reset-modal-btn reset-btn-cancel">${escapeHtml(cancelLabel)}</button>`}
          <button type="button" class="reset-modal-btn reset-btn-confirm">${escapeHtml(confirmLabel)}</button>
        </div>
      </div>
    `;
    document.body.appendChild(modal);
    requestAnimationFrame(() => modal.classList.add('is-visible'));

    const cleanup = (value: boolean) => {
      modal.classList.remove('is-visible');
      window.setTimeout(() => {
        if (modal.parentNode) modal.parentNode.removeChild(modal);
      }, 300);
      resolve(value);
    };

    modal.querySelector('.reset-btn-cancel')?.addEventListener('click', () => cleanup(false));
    modal.querySelector('.reset-btn-confirm')?.addEventListener('click', () => cleanup(true));
    modal.querySelector('.reset-modal-overlay')?.addEventListener('click', () => cleanup(noticeOnly));
  });
}

async function showNotice(title: string, message: string): Promise<void> {
  const fn = (window as any).showBeatlineStyledNotice;
  if (typeof fn === 'function') {
    await fn(title, message);
    return;
  }
  await showBeatlineDialog(title, message, { noticeOnly: true, confirmLabel: 'OK' });
}

async function confirmReset(message: string): Promise<boolean> {
  const fn = (window as any).showResetPlaylistModal;
  if (typeof fn === 'function') {
    return !!(await fn(
      t('modal_reset_title', 'Playlist resetten?'),
      message,
      { confirmLabel: t('btn_reset', 'Reset'), cancelLabel: t('btn_cancel', 'Annuleren') }
    ));
  }
  return showBeatlineDialog(t('modal_reset_title', 'Playlist resetten?'), message, {
    confirmLabel: t('btn_reset', 'Reset'),
    cancelLabel: t('btn_cancel', 'Annuleren')
  });
}

function buildResetPayload(playlistId: string, source: string): Record<string, any> {
  const payload: Record<string, any> = { playlist_id: playlistId, mode: source || 'spotify' };
  if (playlistId.includes(',')) {
    const ids = playlistId.split(',').map((id) => id.trim()).filter(Boolean);
    if (ids.length) {
      payload.playlist_id = ids[0];
      payload.playlist_ids = ids;
    }
  }
  return payload;
}

function setBonusOpen(open: boolean): void {
  try {
    const fn = (window as any).beatlineSetBonusOpen;
    if (typeof fn === 'function') fn(open);
    else document.getElementById('beatline-app')?.classList.toggle('bonus-open', open);
  } catch (_) {}
}

function renderProfileStats(playlists: Record<string, any>): void {
  const content = document.getElementById('profile-content');
  if (!content) return;

  const entries = Object.entries(playlists || {}).filter(([, pl]) => !(pl as any)?.locked);
  if (!entries.length) {
    content.innerHTML = `<p style="text-align:center;color:#aaa;padding:40px 20px;" data-i18n="profile_no_playlists">${escapeHtml(t('profile_no_playlists', 'Geen playlists gevonden.'))}</p>`;
    return;
  }

  let html = '<div class="profile-list">';
  for (const [pid, plRaw] of entries) {
    const pl = plRaw as any;
    const source = displaySourceForProfile(pl);
    const stats = pl.modes && pl.modes[source] ? pl.modes[source] : { heard: 0, total: 0, percentage: 0 };
    const heard = parseInt(String(stats.heard || 0), 10) || 0;
    const total = parseInt(String(stats.total || 0), 10) || 0;
    const percentage = Math.max(0, Math.min(100, parseInt(String(stats.percentage || 0), 10) || 0));
    const imageUrl = pl.image || '';
    const name = pl.name || pid;
    const imagePlaceholder = String(name).charAt(0).toUpperCase();

    html += `
      <div class="profile-item">
        <div class="profile-item-content">
          <div class="profile-item-image">
            ${imageUrl ? `<img src="${escapeHtml(imageUrl)}" alt="${escapeHtml(name)}" />` : `<div class="profile-item-placeholder">${escapeHtml(imagePlaceholder)}</div>`}
          </div>
          <div class="profile-item-info">
            <div class="profile-item-header">
              <h3 class="profile-playlist-name">${escapeHtml(name)}</h3>
              <button class="btn btn-ghost btn-small profile-reset-btn" data-playlist-id="${escapeHtml(pid)}" data-source="${escapeHtml(source)}">
                <span data-i18n="profile_reset_btn">${escapeHtml(t('profile_reset_btn', 'Reset'))}</span>
              </button>
            </div>
            <div class="profile-stats">
              <div class="profile-stats-text">
                <strong>${heard}</strong> <span data-i18n="profile_of">${escapeHtml(t('profile_of', 'van'))}</span> ${total} <span data-i18n="profile_heard">${escapeHtml(t('profile_heard', 'gehoord'))}</span> (${percentage}%)
              </div>
              <div class="profile-progress-bar"><div class="profile-progress-fill" style="width:${percentage}%"></div></div>
            </div>
          </div>
        </div>
      </div>`;
  }
  html += '</div>';
  content.innerHTML = html;

  content.querySelectorAll<HTMLButtonElement>('.profile-reset-btn').forEach((btn) => {
    btn.addEventListener('click', async () => {
      const pid = btn.dataset.playlistId || '';
      const source = btn.dataset.source || 'spotify';
      if (!pid) return;
      const ok = await confirmReset(t('profile_reset_confirm', 'Weet je zeker dat je deze playlist wilt resetten?'));
      if (!ok) return;
      const original = btn.innerHTML;
      btn.disabled = true;
      btn.innerHTML = '<span class="loading-spinner" style="width:16px;height:16px;"></span>';
      try {
        const reset = (window as any).beatlineHeardReset;
        if (typeof reset !== 'function') throw new Error('beatlineHeardReset not available');
        const resp = await reset(buildResetPayload(pid, source));
        const data = resp && resp.success ? resp.data : null;
        if (!data || data.reset !== true) throw new Error(data?.message || 'Reset failed');
        await openProfileModal(configRef);
        await showNotice(t('modal_reset_title', 'Playlist resetten'), t('profile_reset_success', 'Playlist is gereset.'));
      } catch (err: any) {
        await showNotice('Error', String(err?.message || 'Reset failed'));
        btn.disabled = false;
        btn.innerHTML = original;
      }
    });
  });
}

let configRef: ProfileConfig = {};

export async function openProfileModal(config: ProfileConfig = configRef): Promise<void> {
  configRef = config;
  const modal = document.getElementById('profile-modal') as HTMLElement | null;
  const content = document.getElementById('profile-content') as HTMLElement | null;
  if (!modal || !content) {
    await showNotice('Error', 'Modal not found');
    return;
  }

  setBonusOpen(true);
  modal.style.display = 'flex';
  modal.style.zIndex = '10000';

  if (!isLoggedIn(config)) {
    content.innerHTML = `<p style="text-align:center;color:#ff9999;padding:40px 20px;" data-i18n="profile_not_logged_in">${escapeHtml(t('profile_not_logged_in', 'Log in om je profiel te bekijken.'))}</p>`;
    return;
  }

  content.innerHTML = `<div class="profile-loading"><div class="loading-spinner"></div><p data-i18n="profile_loading">${escapeHtml(t('profile_loading', 'Laden...'))}</p></div>`;

  if (!modal.dataset.backdropBoundTs) {
    modal.dataset.backdropBoundTs = '1';
    modal.addEventListener('click', (e) => {
      if (e.target === modal) {
        const close = (window as any).closeModal;
        if (typeof close === 'function') close('profile-modal');
        else modal.style.display = 'none';
      }
    });
  }

  try {
    const statsFn = (window as any).beatlineHeardStats;
    if (typeof statsFn !== 'function') throw new Error('beatlineHeardStats not available');
    const resp = await statsFn();
    if (!resp || !resp.success) {
      const msg = resp?.data?.message || t('profile_not_logged_in', 'Log in om je profiel te bekijken.');
      content.innerHTML = `<p style="text-align:center;color:#ff9999;padding:40px 20px;">${escapeHtml(msg)}</p>`;
      return;
    }
    renderProfileStats(resp.data?.playlists || {});
  } catch (err: any) {
    content.innerHTML = `<p style="text-align:center;color:#ff6b6b;padding:40px 20px;">Error: ${escapeHtml(err?.message || 'Loading failed')}</p>`;
  }
}

export function installProfileController(config: ProfileConfig): void {
  configRef = config;

  const bind = () => {
    const profileBtn = document.getElementById('start-profile-btn') as HTMLElement | null;
    const langBtn = document.getElementById('start-lang-btn') as HTMLElement | null;
    const app = document.getElementById('beatline-app') as HTMLElement | null;
    if (!profileBtn || !langBtn || !app) return;

    if (!profileBtn.dataset.bound) {
      profileBtn.dataset.bound = '1';
      profileBtn.addEventListener('click', async () => {
        if (!isLoggedIn(configRef)) {
          await showNotice(t('profile_title', 'Profiel'), t('profile_not_logged_in', 'Log in om je profiel te bekijken.'));
          return;
        }
        await openProfileModal(configRef);
      });
    }

    const updateVisibility = () => {
      const loggedIn = isLoggedIn(configRef);
      profileBtn.style.display = loggedIn ? 'flex' : 'none';
      langBtn.style.display = loggedIn ? 'none' : 'flex';
      if (loggedIn) {
        const menu = document.getElementById('start-lang-menu');
        if (menu) menu.setAttribute('aria-hidden', 'true');
        langBtn.setAttribute('aria-expanded', 'false');
      }
    };

    updateVisibility();
    window.addEventListener('beatline_data_loaded', updateVisibility);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', bind, { once: true });
  else bind();
}
